import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useNative, useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { goalsApi, type GoalView } from "../../lib/goalsApi";
import { useOnShown } from "../../lib/inApp";
import { lastLoaded, loadKept, prefetchKept } from "../../lib/kept";
import { EditGoal } from "./EditGoal";
import { GoalChatScreen } from "./GoalChat";
import { GoalPage } from "./GoalPage";
import { LogNative } from "./LogNative";
import { LogPage } from "./LogPage";
import { PlanNative } from "./PlanNative";
import { PlanPage } from "./PlanPage";
import { errText } from "./useCc";
import { useCan } from "../../context/AuthContext";

/** How Edit goal reuses the wizard for a goal planned again: where its pages live and where Back goes. */
export interface SetupEdit {
  base: string;
  back: { to: string; label: string };
}

export interface SetupProps {
  /** The path under the goal the wizard should show ("" or "plan"). */
  rest: string;
  edit?: SetupEdit;
  onApproved: (version: number) => void;
}

/**
 * Every page of one goal. A goal with no approved plan yet is still being set up, so the wizard has all of
 * it. Once a plan has been approved (version 1 or later) the goal lives in the Command Center: its page
 * (its results and its chat), plan, log and Edit goal; planning it again runs inside Edit goal. A phone and an
 * iPad draw each page the native way, and the goal's chat has a screen of its own there (`chat`).
 */
/** Start loading a goal as a finger rests on its row, so it opens ready. */
export function prefetchGoal(goalId: string): void {
  prefetchKept(`goal:${goalId}`, () => goalsApi.get(goalId));
}

export function GoalRoutes({ goalId, rest, setup }: { goalId: string; rest: string; setup: (p: SetupProps) => ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const native = useNative();
  const rail = useRail();
  const [view, setView] = useState<GoalView | null>(() => lastLoaded(`goal:${goalId}`));
  const [error, setError] = useState<string | null>(null);
  const can = useCan();

  const reload = useCallback(async () => {
    try {
      setView(await loadKept(`goal:${goalId}`, () => goalsApi.get(goalId)));
      setError(null);
    } catch (e) {
      setError(errText(e));
    }
  }, [goalId]);
  useEffect(() => {
    void reload();
  }, [reload]);
  // Why safe while editing: Edit goal starts its draft once, so a fresh goal under it keeps the edits.
  useOnShown(() => void reload());

  const [page, sub, more] = rest.split("/");
  const goal = `/goals/${encodeURIComponent(goalId)}`;

  if (!view) {
    if (native)
      return (
        <div className="pn-screen">
          <PhoneNav title="" back={page ? { to: goal, label: "Goal" } : rail ? undefined : { to: "/command", label: "Home" }} />
          <p className="pn-empty">{error ?? "Loading the goal…"}</p>
        </div>
      );
    return <div className="wrap ga gw">{<p className="load-note">{error ?? "Loading the goal…"}</p>}</div>;
  }

  const g = view.goal;
  // Why: setting a goal up and changing it are an Editor's; a Viewer sees a goal once its plan is approved.
  const mayEdit = can("editor");

  if (g.version === 0 && !mayEdit) return <Navigate to="/command" replace />;
  if (g.version === 0)
    return setup({
      rest,
      onApproved: () => {
        void reload();
        // Why: a Meta goal goes on to who sees the ads; a cold email goal gets ready on its own page.
        if (g.setup?.channels.meta?.on) navigate(`${goal}/audience`);
        else navigate(`${goal}/ready`, { state: { said: "Plan approved. Next: get ready." } });
      },
    });

  if (page === "edit") {
    if (!mayEdit) return <Navigate to={goal} replace />;
    if (g.state === "planning" || g.state === "plan_ready")
      return setup({
        rest: sub === "plan" ? "plan" : "",
        edit: { base: `${goal}/edit`, back: { to: goal, label: "Goal" } },
        onApproved: () => {
          void reload().then(() => navigate(goal));
        },
      });
    if (g.state === "done") return <Navigate to={goal} replace />;
    return <EditGoal key={`${g.state}:${g.version}`} view={view} reload={reload} />;
  }
  if (page === "plan") return native ? <PlanNative view={view} reload={reload} sub={sub} more={more} /> : <PlanPage view={view} reload={reload} />;
  if (page === "log") return native ? <LogNative view={view} /> : <LogPage view={view} />;
  // Why: on a computer the chat docks beside the goal's page, so its own address opens the page with the chat open.
  if (page === "chat") return native ? <GoalChatScreen key={goalId} view={view} reload={reload} /> : <GoalPage key={goalId} view={view} reload={reload} chatOpen />;
  // Why: a goal's page is its results and its one chat (Command Center V2); old links to Results or a thread land there.
  if (page === "results" || (page === "t" && sub && !more)) return <Navigate to={goal} replace />;
  // Why: a question asked from Home arrives with its answer on the way; on a phone the answer shows in the chat's own screen.
  if (native && (location.state as { runId?: string } | null)?.runId) return <Navigate to={`${goal}/chat`} replace state={location.state} />;
  return <GoalPage key={goalId} view={view} reload={reload} />;
}
